import React, { useEffect, useState } from 'react';
import { ElevatedButton } from '@producer/ui-kit';

import { EditCoverButton } from '../../features/projects/components/edit-cover.button';
import { PhotoUploader } from '../photo-uploader/photo-uploader';
import { PhotoCropperModal } from '../photo-cropper-modal/photo-cropper.modal';
import { ProfilePerson } from '../icons/ProfilePerson';
import { selectFiles } from '../../core/services/file.service';

import styles from './ProfileAvatar.module.scss';
export type ProfilePictureProps = {
  placeholder: 'text' | 'image';
  onChange?: (file: File) => void;
  defaultUrl?: string;
  imageHasError?: boolean;
  onImageLoadError?: () => void;
  initials?: string;
  aspectRatio?:
    | number
    | {
        minimum: number;
        maximum: number;
      };
  theme?: 'light' | 'dark';
};

export const ProfileAvatar: React.FC<ProfilePictureProps> = ({
  placeholder = 'text',
  onChange,
  defaultUrl,
  imageHasError,
  onImageLoadError,
  initials,
  aspectRatio = 1,
  theme,
}) => {
  const [url, setUrl] = useState<string | undefined>();
  const [imgFile, setImgFile] = useState<File | undefined>();
  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>();

  useEffect(() => {
    setUrl(defaultUrl);
  }, [defaultUrl]);

  const handleCropperChange = async (file: File) => {
    setUrl(URL.createObjectURL(file));
    onChange && onChange(file);
  };

  const handleFileChange = async (file: File) => {
    if (!file) {
      return;
    }
    setImgFile(file);
    setIsImageModalOpen(true);
  };

  const handleSelectFile = async () => {
    const files = await selectFiles({ accept: 'image/*', multiple: false });

    if (!files) {
      return;
    }

    const file = files![0];
    await handleFileChange(file);
  };

  return (
    <div className={styles['container']}>
      {placeholder === 'text' && (
        <>
          {url && (
            <div className={styles['user-photo-container']} data-theme={theme}>
              {!imageHasError ? (
                <img className={styles['user-photo']} src={url} onError={onImageLoadError} />
              ) : (
                <div className={styles['initials-container']}>
                  {initials && <div className={styles['initials']}>{initials}</div>}
                </div>
              )}
              {onChange && <EditCoverButton onChange={handleFileChange} />}
            </div>
          )}

          {!url &&
            (onChange ? (
              <PhotoUploader onChange={handleFileChange} />
            ) : (
              <div className={styles['initials-container']}>
                {initials && <div className={styles['initials']}>{initials}</div>}
              </div>
            ))}
        </>
      )}

      {placeholder === 'image' && (
        <div className={styles['content']}>
          <div className={styles['image-placeholder-wrapper']} data-theme={theme}>
            {!imageHasError ? (
              url ? (
                <img className={styles['user-photo']} src={url} onError={onImageLoadError} />
              ) : (
                <ProfilePerson />
              )
            ) : (
              <div className={styles['initials-container']}>
                {initials && <div className={styles['initials']}>{initials}</div>}
              </div>
            )}
          </div>

          <ElevatedButton onClick={handleSelectFile} text={url ? 'Change picture' : 'Add photo'} />
        </div>
      )}

      {isImageModalOpen && (
        <PhotoCropperModal
          theme={theme}
          imgFile={imgFile}
          onCancel={() => setIsImageModalOpen(false)}
          onSave={handleCropperChange}
          aspectRatio={aspectRatio}
        />
      )}
    </div>
  );
};
